<script setup lang="ts">
import {
  Autoplay,
  Controller,
  FreeMode,
  Navigation,
  Virtual,
  Pagination,
  Mousewheel,
} from "swiper/modules";
import { onMounted, ref } from "vue";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import { Swiper, SwiperSlide, useSwiper } from "swiper/vue";
import Control from "@/components/UI/CustomSwiperComponents/CustomSwiperControl.vue";
import { usePopularBrandsQuery, type PopularBrandsQuery } from "@/queries/brands";
import BrandsSkeleton from "@/widgets/home/PopularBrands/BrandsSkeleton.vue";
import DeferredContent from "primevue/deferredcontent";
import YellowAiTip from "@/widgets/common/YellowAiTip.vue";
import { useI18n } from "vue-i18n";
import { computed } from "vue";

const { t } = useI18n();
const swiperModules = [
  FreeMode,
  Autoplay,
  Navigation,
  Controller,
  Virtual,
  Pagination,
  Mousewheel,
];

const params: PopularBrandsQuery = {
  page_num: 1,
  page_size: 16,
};

const popularBrandsQuery = usePopularBrandsQuery(params);
const brands = computed(() => popularBrandsQuery.data.value?.brands ?? []);
const isBrandsLoading = computed(() => popularBrandsQuery.isLoading.value);

async function loadBrandsData() { 
  await popularBrandsQuery.refresh();
}
</script>

<template>
  <DeferredContent @load="loadBrandsData">
    <div class="popular-brands__wrapper">
      <brands-skeleton v-if="isBrandsLoading" />
      <div v-else class="swp-wrp">
        <swiper
          :free-mode="true"
          :space-between="20"
          :slides-offset-after="20"
          :slidesPerGroup="9"
          :mousewheel="{
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
          }"
          :css-mode="false"
          :pagination="{
            clickable: true,
          }"
          :breakpoints="{
            360: {
              slidesPerView: 4,
            },
            768: {
              slidesPerView: 5.5,
            },
            1024: {
              slidesPerView: 7.5,
            },
            1920: {
              slidesPerView: 9,
            },
          }"
          :modules="swiperModules"
          class="company-carousel__container"
        >
          <template #container-start>
            <div class="popular-brands__header">
              <div class="popular-brands__header-title">
                <span class="popular-brands__title h1">{{
                  t("home_page.your_favorite_brands")
                }}</span>
                <yellow-ai-tip :ai-tip-text="t('ai_tips.based_on_most_viewed')" />
              </div>
              <control
                :control-color="'rgba(35, 47, 69, 1)'"
                :group-of-slides-number="9"
              />
            </div>
          </template>
          <swiper-slide
            v-for="(company, index) in brands"
            :key="company.id ?? index"
            class="company-carousel__item"
          >
            <div v-if="company?.background" class="adv-images__wrapper">
              <img class="adv-images" :src="company?.background" />
              <div class="logo-img">
                <img class="logo-brand" :src="company.logoUrl" />
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </div>
    </div>
  </DeferredContent>
</template>

<style scoped>
.popular-brands__wrapper {
  padding: 80px 0 0 70px;
}

:deep(.popular-brands__header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 220px 20px 0;
}

.popular-brands__header-title {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.adv-images__wrapper:hover .adv-images {
  transform: scale(1.2);
  transition: transform 0.4s ease-in-out;
}

.adv-images__wrapper {
  border-radius: 40px;
  cursor: pointer;
  width: 100%;
  height: 100%;
  object-fit: fill;
  position: relative;
}

.logo-brand {
  width: 100%;
}

.adv-images {
  width: 100%;
  height: 100%;
  transition: transform 0.4s ease-in-out;
  object-fit: cover;
  border-radius: 40px;
}

.logo-img {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  position: absolute;
}

.company-carousel__item {
  height: 240px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  border-radius: 40px;
  text-align: center;
  width: auto;
  overflow: hidden;
  cursor: grab;
}

.company-carousel__item:active {
  cursor: grabbing;
}

:deep(.swiper-pagination-bullet) {
  width: 24px;
  height: 4px;
  border-radius: 100px;
}

:deep(.swiper-wrapper) {
  padding-bottom: 40px;
}

.company-carousel__container {
  user-select: none;
  touch-action: pan-y;
}

@media (max-width: 1024px) {
  .popular-brands__wrapper {
    margin-top: 80px;
    padding: 20px;
    background: transparent;
  }

  .adv-images {
    max-height: 124px;
  }

  .popular-brands__header {
    padding: 0;
  }

  .company-carousel__item {
    max-height: 124px;
    border-radius: 28px;
  }

  :deep(.popular-brands-control) {
    display: none;
  }

  :deep(.swiper-wrapper) {
    padding-top: 20px;
  }
}
</style>

<style>
.swiper-wrapper {
  -webkit-transition-timing-function: linear !important;
  -o-transition-timing-function: linear !important;
  transition-timing-function: linear !important;
}
</style>
